import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Check, AlertTriangle, Camera, Printer } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { usePackingStore } from '../stores/packingStore';
import { useAppStore } from '../stores/appStore';

export const PackingPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    currentPackingTask,
    packingItems,
    qualityChecks,
    startPackingTask,
    updatePackingItem,
    addQualityCheck,
    completePackingTask,
    generateShippingLabel,
    isLoading 
  } = usePackingStore();
  const { addNotification } = useAppStore();

  useEffect(() => {
    // Load a demo packing task
    startPackingTask('pack-1');
  }, []);

  const handleItemPacked = async (itemId: string, packed: boolean) => {
    try {
      await updatePackingItem(itemId, packed);
      
      if (packed) {
        addNotification({
          type: 'success',
          title: 'Item Packed',
          message: 'Item marked as packed successfully',
          priority: 'low'
        });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to update packing status',
        priority: 'medium'
      });
    }
  };

  const handleQualityCheck = async () => {
    if (!currentPackingTask) return;
    
    try {
      await addQualityCheck(currentPackingTask.id);
      
      addNotification({
        type: 'success',
        title: 'Quality Check Complete',
        message: 'Package passed quality inspection',
        priority: 'medium'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to complete quality check',
        priority: 'medium'
      });
    }
  };

  const handleGenerateLabel = async () => {
    if (!currentPackingTask) return;
    
    try {
      const labelUrl = await generateShippingLabel(currentPackingTask.id);
      
      addNotification({
        type: 'success',
        title: 'Label Generated',
        message: 'Shipping label is ready for printing',
        priority: 'medium'
      });
      
      // In a real app, this would open the label for printing
      window.open(labelUrl, '_blank');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to generate shipping label',
        priority: 'medium'
      });
    }
  };

  const handleCompleteTask = async () => {
    if (!currentPackingTask) return;
    
    try {
      await completePackingTask(currentPackingTask.id);
      
      addNotification({
        type: 'success',
        title: 'Packing Complete',
        message: 'Package is ready for shipping',
        priority: 'high'
      });
      
      navigate('/');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to complete packing task',
        priority: 'medium'
      });
    }
  };

  const allItemsPacked = packingItems.every(item => item.packed);
  const hasQualityCheck = qualityChecks.length > 0;

  if (isLoading) {
    return (
      <LoadingSpinner />
    );
  }

  if (!currentPackingTask) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Packing Task Found
          </h2>
          <p className="text-gray-600 mb-4">
            No packing tasks are currently assigned.
          </p>
          <Button onClick={() => navigate('/')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 pb-20 sm:pb-24 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            icon={<ArrowLeft className="w-4 h-4" />}
          />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Packing Station
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Pack and prepare orders for shipping
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Badge variant="primary">
            {currentPackingTask.status.replace('_', ' ')}
          </Badge>
          <Badge variant="neutral">
            {currentPackingTask.containerType}
          </Badge>
        </div>
      </motion.div>

      {/* Packing Instructions */}
      {currentPackingTask.packingInstructions && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card>
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Packing Instructions</h3>
                <p className="text-gray-600">{currentPackingTask.packingInstructions}</p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Packing Items */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Items to Pack ({packingItems.filter(item => item.packed).length}/{packingItems.length})
        </h2>

        <div className="space-y-4">
          {packingItems.map((item) => (
            <Card key={item.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    ${item.packed 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-600'
                    }
                  `}>
                    {item.packed ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Package className="w-5 h-5" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{item.productName}</h3>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                    
                    {item.specialHandling && (
                      <div className="flex items-center space-x-1 mt-1">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span className="text-sm text-orange-600">{item.specialHandling}</span>
                      </div>
                    )}
                    
                    {item.fragile && (
                      <Badge variant="warning" size="sm">
                        Fragile
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Camera className="w-4 h-4" />}
                  >
                    Photo
                  </Button>
                  
                  <Button
                    onClick={() => handleItemPacked(item.id, !item.packed)}
                    variant={item.packed ? "outline" : "primary"}
                    size="sm"
                  >
                    {item.packed ? 'Unpack' : 'Pack'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </motion.div>

      {/* Quality Control */}
      {allItemsPacked && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quality Control
            </h3>
            
            {hasQualityCheck ? (
              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                <Check className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Quality Check Passed</p>
                  <p className="text-sm text-green-700">Package is ready for shipping</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600">
                  Perform a final quality check before completing the packing task.
                </p>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleQualityCheck}
                    variant="secondary"
                    icon={<Check className="w-4 h-4" />}
                  >
                    Pass Quality Check
                  </Button>
                  
                  <Button
                    variant="outline"
                    icon={<Camera className="w-4 h-4" />}
                  >
                    Take Photo
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      )}

      {/* Shipping Label */}
      {allItemsPacked && hasQualityCheck && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Shipping Label
            </h3>
            
            {currentPackingTask.shippingLabel ? (
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Printer className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">Label Generated</p>
                    <p className="text-sm text-blue-700">Ready for printing</p>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(currentPackingTask.shippingLabel!, '_blank')}
                >
                  Print Label
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleGenerateLabel}
                icon={<Printer className="w-4 h-4" />}
              >
                Generate Shipping Label
              </Button>
            )}
          </Card>
        </motion.div>
      )}

      {/* Complete Task */}
      {allItemsPacked && hasQualityCheck && currentPackingTask.shippingLabel && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="text-center">
            <Check className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Ready for Shipping!
            </h3>
            <p className="text-gray-600 mb-4">
              All items packed, quality checked, and labeled.
            </p>
            <Button
              onClick={handleCompleteTask}
              size="lg"
              className="w-full"
              icon={<Check className="w-4 h-4" />}
            >
              Complete Packing Task
            </Button>
          </Card>
        </motion.div>
      )}
    </div>
  );
};